import { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Switch, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Chip, Alert, CircularProgress,
  IconButton, Tooltip, InputAdornment, Checkbox, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { api } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import { UserRole } from '../../../shared/types.js';
import type { ICheckPack, ICategory, ICheck, ILabel } from '../../../shared/types.js';
import { colors } from '../../styles/theme.js';
import EmojiPicker from '../../components/EmojiPicker.js';

type PackForm = {
  internal_name: string;
  display_name: string;
  description: string;
  category_id: string;
  emoji: string | null;
};

const emptyForm = (): PackForm => ({ internal_name: '', display_name: '', description: '', category_id: '', emoji: null });

export default function CheckPacksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;
  const [packs, setPacks] = useState<ICheckPack[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [allChecks, setAllChecks] = useState<ICheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCat, setFilterCat] = useState<string>('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<PackForm>(emptyForm);

  const [editTarget, setEditTarget] = useState<ICheckPack | null>(null);
  const [editForm, setEditForm] = useState<PackForm>(emptyForm);

  const [allLabels, setAllLabels] = useState<ILabel[]>([]);

  const [assignTarget, setAssignTarget] = useState<ICheckPack | null>(null);
  const [assignSearch, setAssignSearch] = useState('');
  const [selectedCheckIds, setSelectedCheckIds] = useState<Set<string>>(new Set());
  const [assignLabelFilter, setAssignLabelFilter] = useState<Set<number>>(new Set());
  const [deactivateTarget, setDeactivateTarget] = useState<ICheckPack | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ICheckPack | null>(null);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    const [packsRes, catsRes, checksRes, labelsRes] = await Promise.all([
      api.get<{ check_packs: ICheckPack[] }>('/config/check-packs'),
      api.get<{ categories: ICategory[] }>('/config/categories'),
      api.get<{ checks: ICheck[] }>('/config/checks'),
      api.get<{ labels: ILabel[] }>('/config/labels'),
    ]);
    if (packsRes.success) setPacks(packsRes.data.check_packs);
    if (catsRes.success) setCategories(catsRes.data.categories);
    if (checksRes.success) setAllChecks(checksRes.data.checks);
    if (labelsRes.success) setAllLabels(labelsRes.data.labels);
    if (!packsRes.success) setError(packsRes.error.message);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const filteredPacks = useMemo(() => {
    if (!filterCat) return packs;
    return packs.filter((p) => p.category_id === filterCat);
  }, [packs, filterCat]);

  const handleCreate = async () => {
    const res = await api.post('/config/check-packs', {
      internal_name: createForm.internal_name.trim(),
      display_name: createForm.display_name.trim(),
      description: createForm.description.trim() || null,
      category_id: createForm.category_id,
      emoji: createForm.emoji,
    });
    if (res.success) { setCreateOpen(false); setCreateForm(emptyForm()); loadData(true); }
    else setError(res.error.message);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    const res = await api.patch(`/config/check-packs/${editTarget.id}`, {
      display_name: editForm.display_name.trim(),
      description: editForm.description.trim() || null,
      category_id: editForm.category_id,
      emoji: editForm.emoji,
    });
    if (res.success) { setEditTarget(null); loadData(true); }
    else setError(res.error.message);
  };

  const handleToggleActive = async (pack: ICheckPack) => {
    if (pack.is_active) {
      setDeactivateTarget(pack);
      return;
    }
    setPacks((prev) => prev.map((p) => p.id === pack.id ? { ...p, is_active: true } : p));
    const res = await api.patch(`/config/check-packs/${pack.id}`, { is_active: true });
    if (!res.success) loadData(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateTarget) return;
    const pack = deactivateTarget;
    setDeactivateTarget(null);
    setPacks((prev) => prev.map((p) => p.id === pack.id ? { ...p, is_active: false } : p));
    const res = await api.patch(`/config/check-packs/${pack.id}`, { is_active: false });
    if (!res.success) loadData(true);
  };

  const handleConfirmDeletePack = async () => {
    if (!deleteTarget) return;
    const pack = deleteTarget;
    setDeleteTarget(null);
    const res = await api.delete(`/config/check-packs/${pack.id}`);
    if (res.success) {
      loadData(true);
    } else {
      setError((res as { success: false; error: { message: string } }).error.message);
    }
  };

  const handleDuplicate = async (pack: ICheckPack) => {
    const res = await api.post(`/config/check-packs/${pack.id}/duplicate`, {});
    if (res.success) loadData(true);
    else setError(res.error.message);
  };

  const openAssign = (pack: ICheckPack) => {
    setAssignTarget(pack);
    setAssignSearch('');
    setAssignLabelFilter(new Set());
    const existing = new Set((pack.pack_checks ?? []).map((pc) => pc.check_id));
    setSelectedCheckIds(existing);
  };

  const toggleAssignLabel = (labelId: number) => {
    setAssignLabelFilter((prev) => {
      const next = new Set(prev);
      if (next.has(labelId)) next.delete(labelId);
      else next.add(labelId);
      return next;
    });
  };

  const availableChecks = useMemo(() => {
    if (!assignTarget) return [];
    let filtered = allChecks;
    if (assignLabelFilter.size > 0) {
      filtered = filtered.filter((c) =>
        (c.labels ?? []).some((l) => assignLabelFilter.has(l.id))
      );
    }
    const q = assignSearch.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((c) => c.check_code.toLowerCase().includes(q) || c.title.toLowerCase().includes(q));
    }
    return filtered;
  }, [assignTarget, allChecks, assignSearch, assignLabelFilter]);

  const handleToggleCheck = (checkId: string) => {
    setSelectedCheckIds((prev) => {
      const next = new Set(prev);
      if (next.has(checkId)) next.delete(checkId);
      else next.add(checkId);
      return next;
    });
  };

  const handleSaveAssignment = async () => {
    if (!assignTarget) return;
    const existingIds = new Set((assignTarget.pack_checks ?? []).map((pc) => pc.check_id));
    const toAdd = [...selectedCheckIds].filter((id) => !existingIds.has(id));
    const toRemove = [...existingIds].filter((id) => !selectedCheckIds.has(id));

    for (const id of toRemove) {
      await api.delete(`/config/check-packs/${assignTarget.id}/checks/${id}`);
    }
    if (toAdd.length > 0) {
      await api.post(`/config/check-packs/${assignTarget.id}/checks`, { check_ids: toAdd });
    }

    setAssignTarget(null);
    loadData(true);
  };

  const catName = (id: string) => categories.find((c) => c.id === id)?.display_name ?? id;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Kategorie-Filter</InputLabel>
          <Select value={filterCat} label="Kategorie-Filter" onChange={(e) => setFilterCat(e.target.value)}>
            <MenuItem value="">Alle</MenuItem>
            {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.display_name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Neues Check Pack
        </Button>
      </Box>

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}` }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: colors.lightGrey }}>
              <TableCell sx={{ width: 48 }}>Emoji</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Kategorie</TableCell>
              <TableCell align="center">Checks</TableCell>
              <TableCell align="center">Aktiv</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPacks.map((pack) => (
              <TableRow key={pack.id} hover>
                <TableCell sx={{ fontSize: 22, textAlign: 'center' }}>
                  {pack.emoji?.startsWith('/uploads/')
                    ? <Box component="img" src={pack.emoji} alt="" sx={{ width: 28, height: 28, objectFit: 'contain' }} />
                    : (pack.emoji || '—')}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{pack.display_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{pack.internal_name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={catName(pack.category_id)} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Chip label={pack.pack_checks?.length ?? 0} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  <Switch checked={pack.is_active} onChange={() => handleToggleActive(pack)} size="small" />
                </TableCell>
                <TableCell>
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small" onClick={() => { setEditTarget(pack); setEditForm({ internal_name: pack.internal_name, display_name: pack.display_name, description: pack.description ?? '', category_id: pack.category_id, emoji: pack.emoji }); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Checks zuordnen">
                    <IconButton size="small" onClick={() => openAssign(pack)}>
                      <PlaylistAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplizieren">
                    <IconButton size="small" onClick={() => handleDuplicate(pack)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Löschen">
                    <IconButton size="small" onClick={() => setDeleteTarget(pack)}>
                      <DeleteOutlineIcon fontSize="small" sx={{ color: colors.scoreRed }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredPacks.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">Keine Check Packs vorhanden</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neues Check Pack</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmojiPicker value={createForm.emoji} onChange={(emoji) => setCreateForm({ ...createForm, emoji })} />
            <Typography variant="body2" color="text.secondary">Emoji auswählen</Typography>
          </Box>
          <TextField label="Internal Name" value={createForm.internal_name} onChange={(e) => setCreateForm({ ...createForm, internal_name: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })} helperText="z.B. ecommerce_consent (lowercase, underscores)" />
          <TextField label="Display Name" value={createForm.display_name} onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })} />
          <TextField label="Beschreibung" multiline rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} />
          <FormControl>
            <InputLabel>Kategorie</InputLabel>
            <Select value={createForm.category_id} label="Kategorie" onChange={(e) => setCreateForm({ ...createForm, category_id: e.target.value })}>
              {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.display_name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!createForm.internal_name.trim() || !createForm.display_name.trim() || !createForm.category_id}>Erstellen</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Check Pack bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmojiPicker
              value={editForm.emoji}
              onChange={(emoji) => setEditForm({ ...editForm, emoji })}
              checkPackId={isAdmin ? editTarget?.id : undefined}
              onUpload={isAdmin ? async (file) => {
                if (!editTarget) return;
                const fd = new FormData();
                fd.append('emoji', file);
                const res = await api.upload<{ emoji: string }>(`/config/check-packs/${editTarget.id}/emoji-upload`, fd);
                if (res.success) {
                  setEditForm((prev) => ({ ...prev, emoji: res.data.emoji }));
                  loadData(true);
                }
              } : undefined}
              onDeleteCustom={isAdmin ? async () => {
                if (!editTarget) return;
                const res = await api.delete(`/config/check-packs/${editTarget.id}/emoji`);
                if (res.success) {
                  setEditForm((prev) => ({ ...prev, emoji: null }));
                  loadData(true);
                }
              } : undefined}
            />
            <Typography variant="body2" color="text.secondary">Emoji auswählen</Typography>
          </Box>
          <TextField label="Display Name" value={editForm.display_name} onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })} />
          <TextField label="Beschreibung" multiline rows={2} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          <FormControl>
            <InputLabel>Kategorie</InputLabel>
            <Select value={editForm.category_id} label="Kategorie" onChange={(e) => setEditForm({ ...editForm, category_id: e.target.value })}>
              {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.display_name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Checks Dialog */}
      <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Checks zuordnen — {assignTarget?.display_name}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          {allLabels.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
              {allLabels.map((label) => {
                const active = assignLabelFilter.has(label.id);
                return (
                  <Chip
                    key={label.id}
                    label={label.name}
                    size="small"
                    onClick={() => toggleAssignLabel(label.id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: active ? (label.color ?? colors.black) : 'transparent',
                      color: active ? '#fff' : colors.black60,
                      border: `1px solid ${active ? (label.color ?? colors.black) : colors.silver}`,
                      '&:hover': { opacity: 0.8 },
                    }}
                  />
                );
              })}
            </Box>
          )}
          <TextField
            fullWidth size="small" placeholder="Suche nach Check-Code oder Titel..."
            value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> } }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '14px',
              },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {availableChecks.length} Checks verfügbar
          </Typography>
          <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {availableChecks.map((check) => (
              <ListItem key={check.id} disablePadding sx={{ cursor: 'pointer' }} onClick={() => handleToggleCheck(check.id)}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox edge="start" checked={selectedCheckIds.has(check.id)} tabIndex={-1} disableRipple size="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2"><strong>{check.check_code}</strong> — {check.title}</Typography>
                      {(check.labels ?? []).map((l) => (
                        <Chip key={l.id} label={l.name} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: l.color ?? colors.lightGrey, color: l.color ? '#fff' : colors.black }} />
                      ))}
                    </Box>
                  }
                  secondary={check.description?.slice(0, 80)}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTarget(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveAssignment}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Check Pack deaktivieren?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du <strong>{deactivateTarget?.display_name}</strong> wirklich deaktivieren?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeactivate}>Deaktivieren</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Check Pack löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du <strong>{deleteTarget?.display_name}</strong> wirklich endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeletePack}>Endgültig löschen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
